import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import {
  Users,
  CreditCard,
  Check,
  AlertCircle,
  GripVertical,
  Edit,
  Eye
} from 'lucide-react';

// Simple, clean booking card for Kanban
const KanbanBookingCard = ({
  booking,
  asset,
  onEdit,
  onView,
  onDragStart,
  onDragEnd,
  isDragging,
  readOnly,
  formatTime,
  formatDate,
  formatCurrency,
  getTimezoneAbbr,
  orgTimezone,
  columnColor
}) => {
  const tzAbbr = getTimezoneAbbr(booking.timezone || orgTimezone);
  const hasTeam = booking.teamMemberId || (booking.teamAssignments?.some(a => a.teamId));
  const isPaid = booking.paymentStatus === 'paid';
  const isRefunded = booking.paymentStatus === 'refunded';

  return (
    <div
      draggable={!readOnly}
      onDragStart={(e) => onDragStart(e, booking)}
      onDragEnd={onDragEnd}
      className={`bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.06)] overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${
        isDragging ? 'opacity-50' : ''
      }`}
      onClick={() => onEdit && onEdit(booking)}
    >
      {/* Colored left border like ResourceCard */}
      <div className="flex">
        <div 
          className="w-1 flex-shrink-0" 
          style={{ backgroundColor: columnColor || '#6B7280' }}
        />
        <div className="flex-1 p-3">
          {/* Header: Date & Time + Payment Status */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-gray-900">{formatDate(booking.date)}</span>
              <span className="text-gray-500">
                {formatTime(booking.startTime)}
                {tzAbbr && <span className="text-xs ml-0.5 text-gray-400">{tzAbbr}</span>}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Payment status badge */}
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                isPaid ? 'bg-green-100 text-green-700' :
                isRefunded ? 'bg-blue-100 text-blue-700' :
                'bg-orange-100 text-orange-700'
              }`}>
                {isPaid ? 'Paid' : isRefunded ? 'Refunded' : 'Unpaid'}
              </span>
              {!readOnly && (
                <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
              )}
            </div>
          </div>

          {/* Customer Name */}
          <p className="font-semibold text-gray-900 truncate mb-2">
            {booking.customerName || 'Unknown Customer'}
          </p>

          {/* Details - simple text format */}
          <div className="space-y-1 text-xs text-gray-600">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Service:</span>
              <span className="font-medium text-gray-700 truncate ml-2">{booking.serviceName || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Asset:</span>
              <span className="font-medium text-gray-700 truncate ml-2">{asset?.name || 'Unassigned'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Team:</span>
              <span className="font-medium text-gray-700 truncate ml-2">
                {hasTeam ? `${booking.teamAssignments?.filter(a => a.teamId).length || 1} assigned` : 'Unassigned'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Party:</span>
              <span className="font-medium text-gray-700">{booking.partySize || 1} guests</span>
            </div>
          </div>

          {/* Footer: Amount & Actions */}
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
            <span className="font-semibold text-gray-900">
              {formatCurrency(booking.totalAmount || booking.totalPrice, booking.currency)}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onView && onView(booking);
                }}
                title="View ticket"
              >
            <Eye className="w-3.5 h-3.5 text-gray-400" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onEdit && onEdit(booking);
            }}
            title="Edit booking"
          >
            <Edit className="w-3.5 h-3.5 text-gray-400" />
          </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * KanbanView - Status-based Kanban board for bookings
 * 
 * Columns: Unassigned → Unpaid → Ready
 * Features: Drag & drop, date filtering, click to edit
 */
const KanbanView = ({
  bookings = [],
  assets = [],
  teamMembers = [],
  roles = [],
  onEdit,
  onView,
  onCheckIn,
  timeFormat = '12h',
  readOnly = false
}) => {
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Get org timezone
  const orgTimezone = localStorage.getItem('orgTimezone') || 'America/Chicago';
  
  const getTimezoneAbbr = (tz) => {
    const tzMap = {
      'America/Chicago': 'CST',
      'America/New_York': 'EST',
      'America/Los_Angeles': 'PST',
      'America/Denver': 'MST',
      'UTC': 'UTC'
    };
    return tzMap[tz] || '';
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    if (timeFormat === '24h') return timeStr.slice(0, 5);
    const period = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes}${period}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Categorize bookings into columns (3 columns - check-in handled separately)
  const columns = useMemo(() => {
    const unassigned = [];
    const unpaid = [];
    const ready = [];

    bookings.forEach(booking => {
      // Check assignment status
      const hasAsset = booking.assetId && booking.assetId !== 'unassigned';
      const hasTeam = booking.teamMemberId || (booking.teamAssignments?.some(a => a.teamId));
      
      // Check if team is required
      const asset = assets?.find(a => a.id === booking.assetId);
      const teamRequired = asset?.teamRequirements?.length > 0 || booking.teamAssignments?.length > 0;
      
      const isFullyAssigned = hasAsset && (!teamRequired || hasTeam);
      
      // Check payment status
      const isPaid = booking.paymentStatus === 'paid' || booking.paymentStatus === 'refunded';

      // Categorize (checked-in bookings go to Ready since we have a separate check-in system)
      if (isFullyAssigned && isPaid) {
        ready.push(booking);
      } else if (isFullyAssigned && !isPaid) {
        unpaid.push(booking);
      } else {
        unassigned.push(booking);
      }
    });

    // Sort each column by date and time
    const sortByDateTime = (a, b) => {
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.startTime || '').localeCompare(b.startTime || '');
    };

    return {
      unassigned: unassigned.sort(sortByDateTime),
      unpaid: unpaid.sort(sortByDateTime),
      ready: ready.sort(sortByDateTime)
    };
  }, [bookings, assets]);

  // Column definitions - 3 columns only (check-in handled by separate system)
  const columnConfig = [
    {
      id: 'unassigned',
      title: 'Unassigned',
      subtitle: 'Needs asset or staff',
      color: '#EF4444',
      icon: AlertCircle,
      bookings: columns.unassigned
    },
    {
      id: 'unpaid',
      title: 'Unpaid',
      subtitle: 'Assigned, needs payment',
      color: '#F97316',
      icon: CreditCard,
      bookings: columns.unpaid
    },
    {
      id: 'ready',
      title: 'Ready',
      subtitle: 'Assigned & paid',
      color: '#22C55E',
      icon: Check,
      bookings: columns.ready
    }
  ];

  // Drag handlers
  const handleDragStart = (e, booking) => {
    setDraggedBooking(booking);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedBooking(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedBooking || readOnly) return;

    // Determine what action to take based on target column
    if (targetColumn === 'checkedIn' && onCheckIn) {
      await onCheckIn(draggedBooking.id, true);
    } else if (onEdit) {
      // For other columns, open edit modal
      onEdit(draggedBooking);
    }

    setDraggedBooking(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {columnConfig.map(column => {
        const Icon = column.icon;
        const isDropTarget = dragOverColumn === column.id && draggedBooking;

        return (
          <div
            key={column.id}
            className={`rounded-lg bg-white shadow-[0_8px_24px_rgba(0,0,0,0.04)] ${
              isDropTarget ? 'ring-2 ring-blue-400 ring-offset-2' : ''
            }`}
            style={{ minHeight: '400px' }}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            {/* Column Header */}
            <div className="p-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-7 h-7 rounded-md flex items-center justify-center"
                    style={{ backgroundColor: column.color + '15' }}
                  >
                    <Icon className="w-4 h-4" style={{ color: column.color }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm" style={{ color: column.color }}>
                      {column.title}
                    </h3>
                    <p className="text-xs text-gray-400">{column.subtitle}</p>
                  </div>
                </div>
                <span 
                  className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ 
                    backgroundColor: column.color + '15',
                    color: column.color
                  }}
                >
                  {column.bookings.length}
                </span>
              </div>
            </div>

            {/* Column Content */}
            <div className="p-2 space-y-2 max-h-[600px] overflow-y-auto">
              {column.bookings.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No bookings
                </div>
              ) : (
                column.bookings.map(booking => (
                  <KanbanBookingCard
                    key={booking.id}
                    booking={booking}
                    asset={assets?.find(a => a.id === booking.assetId)}
                    onEdit={onEdit}
                    onView={onView}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    isDragging={draggedBooking?.id === booking.id}
                    readOnly={readOnly}
                    formatTime={formatTime}
                    formatDate={formatDate}
                    formatCurrency={formatCurrency}
                    getTimezoneAbbr={getTimezoneAbbr}
                    orgTimezone={orgTimezone}
                    columnColor={column.color}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KanbanView;
